import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Mail, Phone, Clock, User, CheckCircle, AlertCircle, Loader } from 'lucide-react';

interface LoanDetails {
  bank: string;
  product_name: string;
  interest_rate: number;
  comparison_rate?: number;
  monthly_payment?: number;
  total_cost?: number;
  loan_term_years?: number;
  loan_amount?: number;
  property_value?: number;
}

interface LeadCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  loanDetails: LoanDetails;
  loanProductId?: string;
  user?: { full_name?: string; email?: string; phone?: string };
  onSignInClick?: () => void;
  customerSituation?: Record<string, string[]>;
}

interface FormData {
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  preferred_contact_method: string;
  preferred_contact_time: string;
  loan_purpose: string;
  notes: string;
}

interface FormErrors {
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
}

const LeadCaptureModal: React.FC<LeadCaptureModalProps> = ({
  isOpen,
  onClose,
  loanDetails,
  loanProductId,
  user,
  onSignInClick,
  customerSituation,
}) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    preferred_contact_method: 'Email',
    preferred_contact_time: 'Anytime',
    loan_purpose: '',
    notes: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [assignedBrokerName, setAssignedBrokerName] = useState<string | null>(null);

  // Reset form when modal opens; pre-fill name/email from user if logged in
  useEffect(() => {
    if (isOpen) {
      setFormData({
        customer_name: user?.full_name?.trim() || '',
        customer_email: user?.email?.trim() || '',
        customer_phone: user?.phone?.trim() || '',
        preferred_contact_method: 'Email',
        preferred_contact_time: 'Anytime',
        loan_purpose: '',
        notes: '',
      });
      setErrors({});
      setSubmitSuccess(false);
      setSubmitError(null);
      setAssignedBrokerName(null);
    }
  }, [isOpen, user?.full_name, user?.email, user?.phone]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.customer_name.trim()) {
      newErrors.customer_name = 'Name is required';
    } else if (formData.customer_name.trim().length < 2) {
      newErrors.customer_name = 'Name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.customer_email.trim()) {
      newErrors.customer_email = 'Email is required';
    } else if (!emailRegex.test(formData.customer_email)) {
      newErrors.customer_email = 'Please enter a valid email address';
    }

    // Phone validation
    const phoneDigits = formData.customer_phone.replace(/\D/g, '');
    if (!formData.customer_phone.trim()) {
      newErrors.customer_phone = 'Phone number is required';
    } else if (phoneDigits.length < 10) {
      newErrors.customer_phone = 'Please enter a valid phone number (at least 10 digits)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const API_BASE = (import.meta.env.DEV && import.meta.env.VITE_USE_API_PROXY)
        ? ''
        : (import.meta.env.VITE_API_GATEWAY_URL || 'http://moneybridge-feature-api.australiaeast.azurecontainer.io:8000');
      
      const response = await fetch(`${API_BASE}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          loan_product_id: loanProductId,
          loan_details: loanDetails,
          loan_purpose: formData.loan_purpose || undefined,
          notes: formData.notes || undefined,
          source: 'comparison_results',
          customer_type: user?.email ? 'authenticated' : 'guest',
          customer_situation: customerSituation || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit lead');
      }

      const result = await response.json();
      setAssignedBrokerName(result.assigned_broker_name || null);
      setSubmitSuccess(true);

    } catch (error) {
      console.error('Error submitting lead:', error);
      setSubmitError('Failed to submit your information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name as keyof FormErrors];
        return newErrors;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Content */}
          <div className="p-6 md:p-8">
            {submitSuccess ? (
              // Success state
              <div className="text-center py-8">
                <div className="mb-4 flex justify-center">
                  <div className="rounded-full bg-green-100 p-3">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Thank You!
                </h3>
                <p className="text-gray-600 mb-4">
                  Your information has been submitted successfully.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>What happens next?</strong><br />
                    {assignedBrokerName
                      ? `${assignedBrokerName} will contact you within 24 hours to discuss your loan options.`
                      : 'A mortgage broker will contact you within 24 hours to discuss your loan options.'}
                  </p>
                </div>
                {user?.email ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-purple-800 mb-3">
                      <strong>Want personalised guidance?</strong> Connect directly with a broker to track your application and get tailored advice.
                    </p>
                    <button
                      onClick={() => {
                        onClose();
                        navigate('/brokers');
                      }}
                      className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
                    >
                      Connect with a Broker
                    </button>
                  </div>
                ) : (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-purple-800 mb-3">
                      <strong>Want to track your enquiry?</strong> Create an account to view your dashboard and connect with a broker.
                    </p>
                    <button
                      onClick={() => {
                        onClose();
                        navigate('/login?returnTo=/customer');
                      }}
                      className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
                    >
                      Sign Up / Sign In
                    </button>
                  </div>
                )}
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 text-sm"
                >
                  Close
                </button>
              </div>
            ) : (
              // Form state
              <>
                {/* Header */}
                <div className="mb-6">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    Get Expert Advice
                  </h2>
                  <p className="text-gray-600">
                    A mortgage broker will contact you to discuss this loan and help you find the best option.
                  </p>
                </div>

                {/* Sign in / Continue as guest (for unauthenticated users) */}
                {onSignInClick && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-700 mb-3">Sign in to pre-fill your details, or continue without signing in.</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Google */}
                      <button type="button" onClick={onSignInClick} aria-label="Sign in with Google" title="Sign in with Google"
                        className="p-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm">
                        <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      </button>
                      {/* Microsoft */}
                      <button type="button" onClick={onSignInClick} aria-label="Sign in with Microsoft" title="Sign in with Microsoft"
                        className="p-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm">
                        <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                          <path fill="#F25022" d="M1 1h10v10H1z"/>
                          <path fill="#00A4EF" d="M13 1h10v10H13z"/>
                          <path fill="#7FBA00" d="M1 13h10v10H1z"/>
                          <path fill="#FFB900" d="M13 13h10v10H13z"/>
                        </svg>
                      </button>
                      {/* Apple */}
                      <button type="button" onClick={onSignInClick} aria-label="Sign in with Apple" title="Sign in with Apple"
                        className="p-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm">
                        <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true" fill="currentColor">
                          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                        </svg>
                      </button>
                      {/* Facebook */}
                      <button type="button" onClick={onSignInClick} aria-label="Sign in with Facebook" title="Sign in with Facebook"
                        className="p-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm">
                        <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                          <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                      </button>
                      <span className="text-gray-400 text-sm ml-1">or continue below</span>
                    </div>
                  </div>
                )}

                {/* Loan summary */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 mb-6 border border-blue-100">
                  <h3 className="font-semibold text-gray-900 mb-2">Loan you're interested in:</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Bank:</span>
                      <span className="ml-2 font-medium">{loanDetails.bank}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Product:</span>
                      <span className="ml-2 font-medium">{loanDetails.product_name}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Rate:</span>
                      <span className="ml-2 font-medium text-blue-600">{loanDetails.interest_rate}%</span>
                    </div>
                    {loanDetails.monthly_payment && (
                      <div>
                        <span className="text-gray-600">Monthly:</span>
                        <span className="ml-2 font-medium">${loanDetails.monthly_payment.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Customer situation summary (from Advanced Search filters) */}
                {customerSituation && Object.keys(customerSituation).length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Your situation (shared with broker):</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      {Object.entries(customerSituation).map(([cat, tags]) => (
                        <div key={cat}><span className="font-medium">{cat}:</span> {tags.join(', ')}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name */}
                  <div>
                    <label htmlFor="customer_name" className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="customer_name"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleChange}
                        className={`block w-full pl-10 pr-3 py-2 border ${
                          errors.customer_name ? 'border-red-300' : 'border-gray-300'
                        } rounded-md focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="John Doe"
                      />
                    </div>
                    {errors.customer_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.customer_name}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="customer_email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        id="customer_email"
                        name="customer_email"
                        value={formData.customer_email}
                        onChange={handleChange}
                        className={`block w-full pl-10 pr-3 py-2 border ${
                          errors.customer_email ? 'border-red-300' : 'border-gray-300'
                        } rounded-md focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="john@example.com"
                      />
                    </div>
                    {errors.customer_email && (
                      <p className="mt-1 text-sm text-red-600">{errors.customer_email}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="customer_phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="tel"
                        id="customer_phone"
                        name="customer_phone"
                        value={formData.customer_phone}
                        onChange={handleChange}
                        className={`block w-full pl-10 pr-3 py-2 border ${
                          errors.customer_phone ? 'border-red-300' : 'border-gray-300'
                        } rounded-md focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="0412 345 678"
                      />
                    </div>
                    {errors.customer_phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.customer_phone}</p>
                    )}
                  </div>

                  {/* Loan Purpose */}
                  <div>
                    <label htmlFor="loan_purpose" className="block text-sm font-medium text-gray-700 mb-1">
                      Loan Purpose
                    </label>
                    <select
                      id="loan_purpose"
                      name="loan_purpose"
                      value={formData.loan_purpose}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select (optional)</option>
                      <option value="purchase">Purchase</option>
                      <option value="refinance">Refinance</option>
                      <option value="investment">Investment</option>
                      <option value="construction">Construction</option>
                    </select>
                  </div>

                  {/* Notes / Message */}
                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                      Message (optional)
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={2}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Tell us about your situation..."
                    />
                  </div>

                  {/* Preferred Contact Method */}
                  <div>
                    <label htmlFor="preferred_contact_method" className="block text-sm font-medium text-gray-700 mb-1">
                      Preferred Contact Method
                    </label>
                    <select
                      id="preferred_contact_method"
                      name="preferred_contact_method"
                      value={formData.preferred_contact_method}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Email">Email</option>
                      <option value="Phone">Phone Call</option>
                      <option value="SMS">Text Message (SMS)</option>
                    </select>
                  </div>

                  {/* Preferred Contact Time */}
                  <div>
                    <label htmlFor="preferred_contact_time" className="block text-sm font-medium text-gray-700 mb-1">
                      Preferred Contact Time
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Clock className="h-5 w-5 text-gray-400" />
                      </div>
                      <select
                        id="preferred_contact_time"
                        name="preferred_contact_time"
                        value={formData.preferred_contact_time}
                        onChange={handleChange}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="Morning">Morning (9am - 12pm)</option>
                        <option value="Afternoon">Afternoon (12pm - 5pm)</option>
                        <option value="Evening">Evening (5pm - 8pm)</option>
                        <option value="Anytime">Anytime</option>
                      </select>
                    </div>
                  </div>

                  {/* Error message */}
                  {submitError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                      <p className="text-sm text-red-800">{submitError}</p>
                    </div>
                  )}

                  {/* Privacy notice */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600">
                      By submitting this form, you agree to be contacted by a mortgage broker regarding your loan inquiry. 
                      Your information will be handled in accordance with our Privacy Policy.
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-md hover:from-blue-700 hover:to-purple-700 transition-all font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader className="w-5 h-5 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit & Get Expert Advice'
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadCaptureModal;
